import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshTenant } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("timeout"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          await refreshTenant();
          setState("success");
          setTimeout(() => navigate("/dashboard", { replace: true }), 1400);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries.current >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, navigate, refreshTenant]);

  return (
    <div className="min-h-screen flex items-center justify-center text-slate-100" style={{ background: "#07080D" }} data-testid="payment-success-page">
      <div className="text-center max-w-md px-6">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-indigo-400 mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">Activating your subscription…</h1>
            <p className="text-slate-400 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">You're all set! 🎉</h1>
            <p className="text-slate-400 mt-2">Your free trial has started. Taking you to your dashboard…</p>
          </>
        )}
        {state === "timeout" && (
          <>
            <AlertCircle className="w-14 h-14 text-amber-400 mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">Still processing</h1>
            <p className="text-slate-400 mt-2">Your payment is being confirmed. It may take a minute to reflect.</p>
            <button onClick={() => navigate("/billing")} className="mt-6 px-5 py-3 rounded-full font-semibold text-white" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }} data-testid="payment-back-billing">Back to billing</button>
          </>
        )}
      </div>
    </div>
  );
}
